import {
  ADD_BLOCK_HINT,
  ADD_BLOCK_LABEL,
  BLOCK_TYPES,
  PAGE_EDITOR_IDS,
} from '@/components/admin/experiences/page-editor/contracts';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { BlockType } from '@matter_extractor/shared';
import {
  CheckSquare,
  CircleDot,
  ClipboardCopy,
  Image as ImageIcon,
  Link,
  MousePointerClick,
  Orbit,
  Plus,
  Star,
  TextCursorInput,
  Type,
  Volume2,
} from 'lucide-react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

/**
 * Le popover des types de bloc - ce qu'un « + » ouvre (refonte modulaire §9.3 ;
 * grill du 2026-09-23 ; issue #136).
 *
 * « Il faudrait pouvoir ajouter un bloc directement dans le temps voulu » : la
 * grille « Ajouter » posait le bloc neuf à la fin du DERNIER temps, et il
 * fallait ensuite le glisser sur sept rangs. Le geste part donc de l'endroit où
 * le bloc doit atterrir - le séparateur du temps - et ce composant est la
 * planche des types, décrochée de la grille pour être montrée deux fois : au
 * « + » d'un séparateur, et sur la page encore vide qui n'a aucun séparateur à
 * porter le sien.
 *
 * ## Les mêmes icônes et les mêmes mots que la grille, d'une seule source
 *
 * `ADD_BLOCK_LABEL` et `ADD_BLOCK_HINT` sont ceux de `contracts.ts`, et la
 * table d'icônes vit ICI plutôt qu'en trois exemplaires : la grille en portait
 * une, la pile en portait une autre, et un neuvième type est arrivé deux fois
 * de suite (#137, #151) sans qu'une copie oubliée ne redevienne rouge. Une
 * seule table, exportée, lue par la pile.
 *
 * ## Une grille de trois colonnes, donc les flèches se déplacent en grille
 *
 * `role="menu"` promet les flèches, et `action-menu.tsx` énonce pourquoi :
 * réclamer le rôle sans les touches est la seule combinaison qui TROMPE. Mais
 * une liste se parcourt en haut-bas, et ceci est une grille : gauche et droite
 * décalent d'un cran, haut et bas d'une RANGÉE - trois - ce qui est ce que
 * l'oeil voit. Sans ça, « bas » depuis « Ajouter un texte » atterrirait sur
 * l'image, qui est à sa droite.
 *
 * Le reste - Échap, le clic dehors sur `mousedown`, le focus rendu au
 * déclencheur - est le régime de `action-menu.tsx`, copié parce qu'il y est
 * argumenté et pas parce qu'il est court.
 */

/**
 * Les onze icônes des types, la seule table du dépôt.
 *
 * `ClipboardCopy` pour le Texte copiable et surtout PAS `Copy` : `Copy` est
 * déjà « Dupliquer ce bloc » dans la gouttière d'une ligne de la pile (§4.2),
 * sur cet écran même. `Orbit` pour le Noyau est l'icône de la maquette
 * `AjouterUnBloc` (noyau.md §17).
 */
export const BLOCK_TYPE_ICON = {
  text: Type,
  image: ImageIcon,
  sound: Volume2,
  button: MousePointerClick,
  input: TextCursorInput,
  checkbox: CheckSquare,
  link: Link,
  copyable: ClipboardCopy,
  noyau: Orbit,
  // The icons of the retour maquette (`editeur-page-retour.html`): a radio dot
  // for the Sélection, a star for the Note (retour-experience.md §4).
  selection: CircleDot,
  note: Star,
} as const;

/** Les colonnes de la planche - ce dont les flèches haut et bas se déplacent. */
const COLUMNS = 3;

export interface BlockTypeMenuProps {
  /**
   * Le nom accessible du déclencheur, et le titre du panneau.
   *
   * Nommé par l'appelant et non fixé ici : le « + » d'un séparateur dit dans
   * quel TEMPS le bloc atterrira (« Ajouter un bloc au temps 2 »), ce que ce
   * composant ne peut pas savoir.
   */
  readonly label: string;
  /**
   * `icon` est le « + » d'une gouttière, qui ne doit prendre aucune place.
   * `named` écrit le libellé en toutes lettres, pour la page vide où le menu
   * EST l'offre - un « + » seul y serait un geste à deviner.
   */
  readonly trigger: 'icon' | 'named';
  readonly disabled: boolean;
  readonly onPick: (type: BlockType) => void;
}

export function BlockTypeMenu({ label, trigger, disabled, onPick }: BlockTypeMenuProps) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const triggerNode = useRef<HTMLElement | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerNode.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        /*
          Arrêtée ici : le corps de l'éditeur écoute les touches de suppression
          sur toute sa surface (§9.3), et une échappée qui ferme ce panneau ne
          doit rien déclencher d'autre en chemin.
        */
        event.stopPropagation();
        close(true);
      }
      if (event.key === 'Tab') close(false);
    }
    function onDown(event: MouseEvent) {
      const target = event.target;
      if (target instanceof Node && container.current?.contains(target)) return;
      close(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, close]);

  /** La première tuile prend le focus à l'ouverture - voir `action-menu.tsx`. */
  useEffect(() => {
    if (!open) return;
    const first = panel.current?.querySelector('[role="menuitem"]');
    if (first instanceof HTMLElement) first.focus();
  }, [open]);

  function moveFocus(event: ReactKeyboardEvent<HTMLDivElement>) {
    const step =
      event.key === 'ArrowRight'
        ? 1
        : event.key === 'ArrowLeft'
          ? -1
          : event.key === 'ArrowDown'
            ? COLUMNS
            : event.key === 'ArrowUp'
              ? -COLUMNS
              : 0;
    if (step === 0 && event.key !== 'Home' && event.key !== 'End') return;
    const tiles = [...(panel.current?.querySelectorAll('[role="menuitem"]') ?? [])].filter(
      (node): node is HTMLButtonElement => node instanceof HTMLButtonElement,
    );
    if (tiles.length === 0) return;
    event.preventDefault();
    const here = tiles.findIndex((node) => node === document.activeElement);
    const last = tiles.length - 1;
    /* Un anneau : « droite » sur la dernière tuile revient à la première. */
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? last
          : ((here < 0 ? 0 : here) + step + tiles.length) % tiles.length;
    tiles[next]?.focus();
  }

  return (
    <div ref={container} className={cn('relative', trigger === 'named' ? '' : 'flex shrink-0')}>
      {trigger === 'icon' ? (
        <Tooltip label={label}>
          {/* Un `span role="button"` et non un `<button>` : le séparateur qui le
              porte est lui-même un `role="button"`, comme les lignes de la pile.
              `stopPropagation` sur le clic, sinon ouvrir le menu sélectionnerait
              le temps du même geste. */}
          {/* biome-ignore lint/a11y/useSemanticElements: a <button> inside a role="button" separator. */}
          <span
            role="button"
            ref={triggerNode}
            tabIndex={0}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            data-testid={PAGE_EDITOR_IDS.blockAdd}
            aria-label={label}
            aria-disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              if (disabled) return;
              setOpen((was) => !was);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              event.stopPropagation();
              if (disabled) return;
              setOpen((was) => !was);
            }}
            className={cn(
              'flex shrink-0 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              disabled
                ? 'cursor-not-allowed text-gray-300'
                : 'cursor-pointer text-gray-400 hover:text-bleu-nuit',
            )}
          >
            <Plus aria-hidden className="size-3.5" />
          </span>
        </Tooltip>
      ) : (
        <button
          ref={(node) => {
            triggerNode.current = node;
          }}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          data-testid={PAGE_EDITOR_IDS.blockAdd}
          disabled={disabled}
          onClick={() => setOpen((was) => !was)}
          className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-gray-300 border-dashed bg-white px-3 py-2 font-semibold text-gray-700 text-xs transition-colors hover:border-bleu-nuit hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Plus aria-hidden className="size-3.5" />
          {label}
        </button>
      )}

      {open ? (
        <div
          id={panelId}
          ref={panel}
          onKeyDown={moveFocus}
          data-testid={PAGE_EDITOR_IDS.blockAddMenu}
          className="absolute right-0 z-20 mt-1 w-60 rounded-md border border-gray-200 bg-white p-1.5 shadow-lg"
        >
          <div role="menu" aria-label={label} className="grid grid-cols-3 gap-1.5">
            {BLOCK_TYPES.map((type) => {
              const Icon = BLOCK_TYPE_ICON[type];
              return (
                <Tooltip key={type} label={ADD_BLOCK_HINT[type]}>
                  <button
                    type="button"
                    role="menuitem"
                    data-testid={PAGE_EDITOR_IDS.blockAddOption}
                    data-block-type={type}
                    onClick={(event) => {
                      /*
                        `stopPropagation` : le panneau est rendu DANS le
                        séparateur, qui est lui-même un `role="button"`, donc
                        sans lui le clic sélectionnerait le temps juste après
                        que le bloc neuf ait pris la sélection - et c'est
                        l'inspecteur du temps qui s'ouvrirait. Même raison que
                        l'icône de copie d'une ligne, un cran plus bas.
                      */
                      event.stopPropagation();
                      /* Le focus rentre AVANT le geste : le panneau se démonte
                         sous la tuile pressée, et le clavier atterrirait sinon
                         sur le corps du document - le régime de
                         `action-menu.tsx`. */
                      close(true);
                      onPick(type);
                    }}
                    className="flex cursor-pointer flex-col items-center gap-1 rounded-md border-2 border-gray-200 bg-white px-1 py-2 text-center font-semibold text-[11px] text-gray-700 leading-3.5 transition-colors hover:border-bleu-nuit hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Icon aria-hidden className="size-4 text-gray-500" />
                    {ADD_BLOCK_LABEL[type]}
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
